import { createSelector } from 'reselect'
import { ReduxState } from '@/utils/typeHelpers'
import {
  OnboardingStepId,
  isGettingStartedUserOnboardingStepExpanded,
  GettingStartedUserOnboardingStepExpanded,
  OnboardingStepPhase,
} from './onboarding.reducer'
import { get } from 'lodash'
import { DateTime } from 'luxon'
import { selectIsCatchUpBookkeepingComplete } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'

export const getUserOnboardingSteps = (state: ReduxState) =>
  state.onboarding.byId

export const getAllOnboardingSteps = (state: ReduxState) =>
  state.onboarding.allOnboardingSteps

export const getUserOnboardingStepByOnboardingStepId = createSelector(
  getUserOnboardingSteps,
  (_: unknown, onboardingStepId?: OnboardingStepId) => onboardingStepId,
  (onboardingSteps, onboardingStepId) => {
    return Object.values(onboardingSteps).find(
      (step) => step.onboardingStepId === onboardingStepId
    )
  }
)

export const getUserStepIdByOnboardingStepId = createSelector(
  getUserOnboardingStepByOnboardingStepId,
  (step) => step?.id
)

export const selectUserOnboardingStepsWithOnboardingSteps = createSelector(
  getUserOnboardingSteps,
  getAllOnboardingSteps,
  (userOnboardingSteps, allOnboardingSteps) => {
    return Object.values(userOnboardingSteps).map((userOnboardingStep) => ({
      ...userOnboardingStep,
      onboardingStep: get(
        allOnboardingSteps,
        userOnboardingStep.onboardingStepId,
        undefined
      ),
    }))
  }
)

export const selectOnboardingStepCompletedOrSkipped = createSelector(
  selectUserOnboardingStepsWithOnboardingSteps,
  (_: unknown, onboardingStepId: OnboardingStepId) => onboardingStepId,
  (userOnboardingSteps, onboardingStepId) => {
    const userOnboardingStep = userOnboardingSteps.find(
      (step) => step.onboardingStep?.identifier === onboardingStepId
    )
    // If the user doesn't have the onboarding step, we consider it completed
    if (!userOnboardingStep) {
      return true
    }
    return Boolean(
      userOnboardingStep.completedAt || userOnboardingStep.skippedAt
    )
  }
)

export const selectCompletedAtForOnboardingStep = createSelector(
  selectUserOnboardingStepsWithOnboardingSteps,
  (_: unknown, onboardingStepId: OnboardingStepId) => onboardingStepId,
  (userOnboardingSteps, onboardingStepId) => {
    const userOnboardingStep = userOnboardingSteps.find(
      (step) => step.onboardingStep?.identifier === onboardingStepId
    )
    return userOnboardingStep?.completedAt
  }
)

const userHasCompletedOnboardingPhase = (
  userOnboardingSteps: ReturnType<typeof getUserOnboardingSteps>,
  allOnboardingSteps: ReturnType<typeof getAllOnboardingSteps>,
  phase: OnboardingStepPhase
) => {
  const onboardingStepsForPhase = Object.values(allOnboardingSteps).filter(
    (step) => step.phase === phase
  )
  const userOnboardingStepsForPhase = Object.values(userOnboardingSteps).filter(
    (userOnboardingStep) =>
      onboardingStepsForPhase
        .map((step) => step.identifier)
        .includes(userOnboardingStep.onboardingStepId)
  )
  // If the user doesn't have any onboarding items for the page, we consider the phase completed
  if (userOnboardingStepsForPhase.length === 0) {
    return true
  }
  return userOnboardingStepsForPhase.every(
    (step) => step.completedAt || step.skippedAt
  )
}

export const selectHasUserCompletedOnboardingPhase = createSelector(
  getUserOnboardingSteps,
  getAllOnboardingSteps,
  (_: unknown, phase: OnboardingStepPhase) => phase,
  (userOnboardingSteps, allOnboardingSteps, phase) => {
    return userHasCompletedOnboardingPhase(
      userOnboardingSteps,
      allOnboardingSteps,
      phase
    )
  }
)

export const selectUserShouldSeeOnboardingDashboard = createSelector(
  getUserOnboardingSteps,
  getAllOnboardingSteps,
  selectIsCatchUpBookkeepingComplete,
  (userOnboardingSteps, allOnboardingSteps, catchupBKIsComplete) => {
    return (
      !userHasCompletedOnboardingPhase(
        userOnboardingSteps,
        allOnboardingSteps,
        OnboardingStepPhase.gettingStarted
      ) || !catchupBKIsComplete
    )
  }
)

export const selectLastStartedAtStepForOnboardingPhase = createSelector(
  selectUserOnboardingStepsWithOnboardingSteps,
  (_: unknown, phase: OnboardingStepPhase) => phase,
  (userOnboardingSteps, phase) => {
    const onboardingStepsForPhase = userOnboardingSteps.filter(
      (step) => step.onboardingStep?.phase === phase
    )
    const lastStartedAtStep = onboardingStepsForPhase
      .filter((step) => step.startedAt)
      .sort((a, b) => {
        if (!a.startedAt || !b.startedAt) {
          return 0
        }
        return (
          DateTime.fromISO(b.startedAt).toMillis() -
          DateTime.fromISO(a.startedAt).toMillis()
        )
      })[0]
    return lastStartedAtStep
  }
)
// Getting Started Steps
export const selectGettingStartedUserOnboardingSteps = createSelector(
  selectUserOnboardingStepsWithOnboardingSteps,
  (userOnboardingSteps) =>
    userOnboardingSteps.filter<GettingStartedUserOnboardingStepExpanded>(
      isGettingStartedUserOnboardingStepExpanded
    )
)

export const selectPartitionedGettingStartedUserOnboardingStepData =
  createSelector(
    selectGettingStartedUserOnboardingSteps,
    (onboardingSteps) => ({
      onboardingSteps,
      stepsCompletedOrSkippedIds: onboardingSteps
        .filter((step) => step.completedAt || step.skippedAt)
        .map((step) => step.onboardingStep.identifier),
    })
  )
